import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  AvailabilityDayDto,
  AvailabilityResponseDto,
  AvailabilitySlotDto,
  BookingRequestDto,
  BookingResponseDto
} from '../../features/onboarding/models/booking.models';

export interface SiteUserMessageSubmitRequest {
  name: string;
  email: string;
  companyName?: string;
  subject?: string;
  messageBody: string;
  sourcePage?: string;
  recaptchaToken: string;
}

export interface SiteUserMessageSubmitResponse {
  success: boolean;
  message: string;
  messageId?: number | null;
}

@Injectable({ providedIn: 'root' })
export class BookingApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = environment.onboardingApiBaseUrl;

  getAvailability(startDate: string, endDate: string, recaptchaToken?: string): Observable<AvailabilityResponseDto> {
    const headers = recaptchaToken
      ? new HttpHeaders({ 'X-Recaptcha-Token': recaptchaToken })
      : undefined;

    return this.http.get<AvailabilityResponseDto | AvailabilitySlotDto[]>(
      `${this.baseUrl}/availability?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`,
      { headers }
    ).pipe(map((response) => this.normalizeAvailabilityResponse(response, startDate, endDate)));
  }

  private normalizeAvailabilityResponse(
    response: AvailabilityResponseDto | AvailabilitySlotDto[],
    startDate: string,
    endDate: string
  ): AvailabilityResponseDto {
    if (Array.isArray(response)) {
      const dayByDate = new Map<string, AvailabilityDayDto>();
      for (const slot of response) {
        const date = slot.startUtc.slice(0, 10);
        dayByDate.set(date, { date, status: 'available' });
      }

      return {
        startDate,
        endDate,
        timeZone: response[0]?.timeZone ?? '',
        days: [...dayByDate.values()],
        slots: response
      };
    }

    return {
      startDate: response.startDate,
      endDate: response.endDate,
      timeZone: response.timeZone,
      days: Array.isArray(response.days) ? response.days : [],
      slots: Array.isArray(response.slots) ? response.slots : []
    };
  }

  submitBooking(request: BookingRequestDto): Observable<BookingResponseDto> {
    return this.http.post<BookingResponseDto>(`${this.baseUrl}/booking-request`, request);
  }

  submitSiteUserMessage(request: SiteUserMessageSubmitRequest): Observable<SiteUserMessageSubmitResponse> {
    return this.http.post<SiteUserMessageSubmitResponse>(`${this.baseUrl}/messages`, request);
  }
}
